// src/pages/customer/CustomerPay.jsx
// Оплата покупки баллами через сканирование QR кассы.
// Шаги:
//   1. Сканер камеры → читаем токен
//   2. Загружаем детали платежа из бэка (товары, сумма)
//   3. Показываем подтверждение → клиент жмёт «Оплатить»
//   4. POST /redeem → списание баллов и финальный чек

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { payments as paymentsApi, loyalty as loyaltyApi } from '../../api';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import Icon from '../../components/Icon';
import { formatMoney } from '../../data/currencies';
import { scanQRFromFile } from '../../utils/qrShare';

function unitLabel(u) {
  if (u === 'kg' || u === 'кг') return 'кг';
  if (u === 'l' || u === 'л')   return 'л';
  return 'шт';
}

export default function CustomerPay() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isGuest } = useAuth();
  const { show, ToastView } = useToast();

  const [stage, setStage] = useState('scan'); // scan | confirm | success | error
  const [scanning, setScanning] = useState(false);
  const [pending, setPending] = useState(null); // детали платежа из бэка
  const [card, setCard]       = useState(null); // карта лояльности клиента в этом магазине
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState('');
  const [paying, setPaying]   = useState(false);
  const html5QrRef = useRef(null);
  const fileInputRef = useRef(null);

  const pickFromGallery = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    try { await stopScan(); } catch (err) {}
    setError('');
    try {
      const decoded = await scanQRFromFile(file);
      await processQR(decoded);
    } catch (err) {
      setError(t('customerScan.errorNoQRInImage'));
      setStage('error');
    }
  };

  // Гость не может оплачивать
  useEffect(() => {
    if (isGuest) {
      show(t('customerPay.errorNotForGuests'), 'error');
      navigate('/');
    }
  }, [isGuest, navigate, show]);

  const startScan = async () => {
    setError('');
    setScanning(true);
    try {
      html5QrRef.current = new Html5Qrcode('qr-pay-reader');
      await html5QrRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          await stopScan();
          await processQR(decodedText);
        },
        () => {}
      );
    } catch (e) {
      setScanning(false);
      setError(t('customerScan.errorCamera'));
      setStage('error');
    }
  };

  const stopScan = async () => {
    if (html5QrRef.current) {
      try { await html5QrRef.current.stop(); html5QrRef.current.clear(); } catch (e) {}
    }
    setScanning(false);
  };

  const processQR = async (text) => {
    try {
      // Токен — это просто строка (не JSON)
      const token = text.trim();
      if (!token) throw new Error(t('customerPay.errorEmptyQR'));

      const data = await paymentsApi.getPending(token);

      if (data.status === 'paid')      throw new Error(t('customerPay.errAlreadyPaid'));
      if (data.status === 'cancelled') throw new Error(t('customerPay.errCancelled'));
      if (data.status === 'expired')   throw new Error(t('cashier.expiredPayment'));

      // Загружаем нашу карту лояльности в этом магазине
      let myCard = null;
      try {
        const cards = await loyaltyApi.cards();
        myCard = cards.find(c => c.shopId === data.shop.id);
      } catch (e) {}

      setPending(data);
      setCard(myCard);
      setStage('confirm');
    } catch (e) {
      setError(e.message || t('customerPay.errorEmptyQR'));
      setStage('error');
    }
  };

  const confirmPayment = async () => {
    if (!pending) return;
    setPaying(true);
    try {
      const res = await paymentsApi.redeemPending(pending.token);
      setResult(res);
      setStage('success');
    } catch (e) {
      setError(e.message || t('common.error'));
      setStage('error');
    }
    setPaying(false);
  };

  useEffect(() => { return () => { stopScan(); }; /* eslint-disable-next-line */ }, []);

  // Авто-старт сканера при первом заходе
  useEffect(() => {
    if (stage === 'scan' && !scanning) {
      const t = setTimeout(() => startScan(), 200);
      return () => clearTimeout(t);
    }
    /* eslint-disable-next-line */
  }, [stage]);

  const points = card?.points || 0;
  const minPoints = pending?.shop?.minPointsToRedeem || 500;
  const amount = pending?.amount || 0;

  // Сколько спишется и сколько доплатить
  const willRedeem  = Math.min(points, Math.floor(amount));
  const willCash    = Math.max(0, amount - willRedeem);
  const canPay      = points >= minPoints && willRedeem > 0;

  return (
    <div className="page" style={{ paddingBottom: 24 }}>
      {ToastView}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <div style={{ flex: 1 }}>
          <p className="page-subtitle">{t("customerPay.subtitle")}</p>
          <h1 className="page-title">
            {stage === 'scan' && t('customerPay.scanTitle')}
            {stage === 'confirm' && t('customerPay.confirmTitle')}
            {stage === 'success' && t('customerPay.successTitle')}
            {stage === 'error' && t('common.error')}
          </h1>
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>

        {/* Шаг 1: сканер */}
        {stage === 'scan' && (
          <>
            <div id="qr-pay-reader" style={{
              width: '100%',
              minHeight: 320,
              background: 'var(--bg-subtle)',
              borderRadius: 'var(--r-md)',
              overflow: 'hidden',
              marginBottom: 12,
            }} />
            <p style={{ fontSize: 13, color: 'var(--fg-muted)', textAlign: 'center', marginTop: 12 }}>
              {t("customerPay.scanHint")}
            </p>
            <button
              className="btn btn-secondary"
              style={{ marginTop: 12, width: '100%' }}
              onClick={pickFromGallery}
            >
              🖼️ {t("customerScan.fromGallery")}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
          </>
        )}

        {/* Шаг 2: подтверждение */}
        {stage === 'confirm' && pending && (
          <>
            {/* Магазин */}
            <div className="card" style={{ marginBottom: 12, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
              {pending.shop.photo ? (
                <img src={pending.shop.photo} alt="" style={{ width: 48, height: 48, borderRadius: 'var(--r-sm)', objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <div style={{
                  width: 48, height: 48, borderRadius: 'var(--r-sm)',
                  background: 'var(--brand-primary-soft)', color: 'var(--brand-primary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}><Icon name="store" size={20} /></div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {pending.shop.name}
                </p>
                <p style={{ fontSize: 12, color: 'var(--fg-faint)' }}>
                  {t("customerPay.balance")}: <strong style={{ color: "var(--brand-accent)" }}>{points.toLocaleString()} ⭐</strong>
                </p>
              </div>
            </div>

            {/* Список товаров */}
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', marginBottom: 8, letterSpacing: '0.08em' }}>
              {t("customerPay.checkLabel")} · {pending.items.length} {pending.items.length === 1 ? t("cashier.position") : (pending.items.length < 5 ? t("cashier.positions") : t("cashier.positionsMany"))}
            </p>
            <div className="card" style={{ marginBottom: 12, padding: 0 }}>
              {pending.items.map((it, idx) => (
                <div key={idx} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 14px',
                  borderBottom: idx < pending.items.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {it.name}
                    </p>
                    <p style={{ fontSize: 11, color: 'var(--fg-faint)', fontVariantNumeric: 'tabular-nums' }}>
                      {it.qty} {unitLabel(it.unit)} × {it.salePrice}
                    </p>
                  </div>
                  <p style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                    {formatMoney(it.qty * it.salePrice, pending?.shop?.currency)}
                  </p>
                </div>
              ))}
            </div>

            {/* Расчёт оплаты */}
            <div className="card" style={{ marginBottom: 14, background: 'var(--brand-primary-soft)', borderColor: 'var(--brand-primary)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{t("customerPay.amount")}</span>
                <span style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                  {formatMoney(amount, pending?.shop?.currency)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--brand-accent)' }}>{t("customerPay.willRedeem")}</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--brand-accent)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                  −{willRedeem.toLocaleString()} ⭐
                </span>
              </div>
              {willCash > 0 && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  paddingTop: 8, borderTop: '1px solid var(--border-subtle)',
                }}>
                  <span style={{ fontSize: 13, color: 'var(--fg)', fontWeight: 600 }}>{t("customerPay.willCash")}</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--fg)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                    {formatMoney(willCash, pending?.shop?.currency)}
                  </span>
                </div>
              )}
            </div>

            {points < minPoints ? (
              <div style={{
                padding: 12, marginBottom: 12,
                background: 'var(--warning-soft)',
                borderRadius: 'var(--r-sm)',
                color: 'var(--warning)',
                fontSize: 13,
                display: 'flex', alignItems: 'flex-start', gap: 8,
              }}>
                <Icon name="alert" size={16} stroke={2} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>
                  {t("customerPay.minPointsWarning", { min: minPoints, current: points })}
                </span>
              </div>
            ) : null}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setStage('scan'); setPending(null); }}
                style={{ flex: 1 }}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary btn-lg"
                onClick={confirmPayment}
                disabled={paying || !canPay}
                style={{ flex: 2 }}
              >
                {paying ? t('customerPay.paying') : t('customerPay.confirmPay')}
              </button>
            </div>
          </>
        )}

        {/* Шаг 3: успех */}
        {stage === 'success' && result && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{
              width: 80, height: 80, borderRadius: 999,
              background: 'var(--success-soft)', color: 'var(--success)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Icon name="check" size={40} stroke={2.5} />
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 6 }}>{t("customerPay.successTitle")}</h2>
            <p style={{ fontSize: 32, fontWeight: 800, color: 'var(--brand-accent)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)', marginBottom: 4 }}>
              −{result.pointsRedeemed?.toLocaleString()} ⭐
            </p>
            {result.cashAmount > 0 && (
              <p style={{ fontSize: 16, color: 'var(--fg-muted)', marginTop: 8 }}>
                {t("customerPay.toCustomer")} <strong style={{ color: "var(--fg)" }}>{formatMoney(result.cashAmount, pending?.shop?.currency)}</strong>
              </p>
            )}
            <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 16 }}>
              {t('customerPay.remainingPoints', { count: result.remainingPoints?.toLocaleString() || 0 })}
            </p>

            <button
              className="btn btn-primary btn-lg"
              onClick={() => navigate('/')}
              style={{ marginTop: 24 }}
            >
              {t("customerPay.toHome")}
              <Icon name="arrowRight" size={18} stroke={2.2} />
            </button>
          </div>
        )}

        {/* Шаг 4: ошибка */}
        {stage === 'error' && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{
              width: 80, height: 80, borderRadius: 999,
              background: 'var(--danger-soft)', color: 'var(--danger)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Icon name="alert" size={40} stroke={2.5} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>{t("customerPay.errorTitle")}</h2>
            <p style={{ fontSize: 14, color: 'var(--fg-muted)', maxWidth: 320, margin: '0 auto 20px' }}>
              {error}
            </p>
            <div style={{ display: 'flex', gap: 8, maxWidth: 320, margin: '0 auto' }}>
              <button className="btn btn-secondary" onClick={() => navigate('/')} style={{ flex: 1 }}>
                {t('common.close')}
              </button>
              <button className="btn btn-primary" onClick={() => { setStage('scan'); setError(''); }} style={{ flex: 1 }}>
                {t("customerPay.errorScanAgain")}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
